/**
 * Bid Data Hooks
 * React Query hooks for fetching and managing bid data
 *
 * Following DDD Principles:
 * - Hooks consume application services
 * - React Query handles caching and state management
 * - Components receive view models ready for display
 * - NO business logic in hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bidApplicationService } from '../BidApplicationService';
import { serviceProviderService } from '@/services/service-provider.service';
import type { BidListViewModel, BidDetailViewModel } from '../view-models/BidViewModel';
import type { CreateBidRequest } from '@calwmp/shared';

/**
 * Bid query key factory
 * Provides consistent cache keys for React Query
 */
export const bidKeys = {
  all: ['bids'] as const,
  lists: () => [...bidKeys.all, 'list'] as const,
  list: (filters?: { status?: string; dateRange?: string }) =>
    [...bidKeys.lists(), { filters }] as const,
  details: () => [...bidKeys.all, 'detail'] as const,
  detail: (id: string) => [...bidKeys.details(), id] as const,
};

/**
 * Hook to fetch bid list with optional filters
 */
export function useBidList(filters?: { status?: string; dateRange?: string }) {
  return useQuery<BidListViewModel, Error>({
    queryKey: bidKeys.list(filters),
    queryFn: () => bidApplicationService.getBidList(filters),
    staleTime: 1000 * 60 * 2, // Consider data fresh for 2 minutes
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });
}

/**
 * Hook to fetch single bid detail
 */
export function useBidDetail(bidId: string | undefined) {
  return useQuery<BidDetailViewModel, Error>({
    queryKey: bidKeys.detail(bidId ?? ''),
    queryFn: () => {
      if (!bidId) {
        throw new Error('Bid ID is required');
      }
      return bidApplicationService.getBidDetail(bidId);
    },
    enabled: !!bidId, // Only run query if bidId exists
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    retry: 2,
  });
}

/**
 * Hook to submit a new bid
 * Uses mutation for write operations
 */
export function useSubmitBid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, bid }: { planId: string; bid: CreateBidRequest }) =>
      serviceProviderService.submitBid(planId, bid),
    onSuccess: () => {
      // Invalidate bid queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: bidKeys.all });
      // Also invalidate dashboard since metrics may have changed
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Hook to update an existing bid
 * Uses mutation for write operations
 */
export function useUpdateBid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bidId, updates }: { bidId: string; updates: Partial<CreateBidRequest> }) =>
      serviceProviderService.updateBid(bidId, updates),
    onSuccess: (_data, variables) => {
      // Invalidate specific bid detail
      queryClient.invalidateQueries({ queryKey: bidKeys.detail(variables.bidId) });
      // Invalidate bid lists
      queryClient.invalidateQueries({ queryKey: bidKeys.lists() });
    },
  });
}

/**
 * Hook to get all bids (no filters)
 * Useful for dashboard overview
 */
export function useAllBids() {
  return useBidList();
}

/**
 * Hook to get pending bids only
 * Useful for "awaiting response" views
 */
export function usePendingBids() {
  return useBidList({ status: 'pending' });
}

/**
 * Hook to get accepted bids only
 * Useful for "won bids" views
 */
export function useAcceptedBids() {
  return useBidList({ status: 'accepted' });
}

/**
 * Hook to get rejected bids only
 * Useful for "lost bids" views
 */
export function useRejectedBids() {
  return useBidList({ status: 'rejected' });
}
